import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import Select from 'react-select';
import './ComparisonTemplate.css';
import frm from './formAux.js';

const ComparisonTemplate = forwardRef( (props,ref) => {
  const { currentIndex, allPrompts } = props;

  // --- LOCAL COMPONENT STATE ---
  // These hold the data for the currently viewed prompt.
  const [currentRatings, setCurrentRatings] = useState({ response1: {}, response2: {} });
  const [currentExplanation, setCurrentExplanation] = useState('');
  const [currentCorrectedResponse1, setCurrentCorrectedResponse1] = useState('');
  const [currentCitation1, setCurrentCitation1] = useState('');
  const [currentCorrectedResponse2, setCurrentCorrectedResponse2] = useState('');
  const [currentCitation2, setCurrentCitation2] = useState('');
  const [currentResponseRanking, setCurrentResponseRanking] = useState(null);

  // --- OTHER STATE & CONSTANTS ---
  const isTaskLocked = () => window.Utterancedata?.descriptor?.blockEdit;
  const [isLocked, setIsLocked] = useState(() => isTaskLocked() || false);
  const isInitialized = allPrompts && allPrompts.length > 0;

  const ratingOptions = {
    overallQuality: [ { value: 'Great', label: 'Great' }, { value: 'Neutral', label: 'Neutral' }, { value: 'Poor', label: 'Poor' }, { value: 'Unratable', label: 'Unratable' } ],
    writingStyle: [ { value: 'No issues', label: 'No issues' }, { value: 'Minor issues', label: 'Minor issues' }, { value: 'Major issues', label: 'Major issues' } ],
    verbosity: [ { value: 'Overly verbose', label: 'Overly verbose' }, { value: 'Just Right', label: 'Just Right' }, { value: 'Too short', label: 'Too short' } ],
    instructionFollowing: [ { value: 'No issues', label: 'No issues' }, { value: 'Minor issues', label: 'Minor issues' }, { value: 'Major issues', label: 'Major issues' }, { value: 'N/A', label: 'N/A' } ],
    truthfulness: [ { value: 'No issues', label: 'No issues' }, { value: 'Minor issues', label: 'Minor issues' }, { value: 'Major issues', label: 'Major issues' }, { value: 'Cannot Determine', label: 'Cannot Determine' } ],
    harmlessness: [ { value: 'No issues', label: 'No issues' }, { value: 'Minor issues', label: 'Minor issues' }, { value: 'Major issues', label: 'Major issues' } ],
    ranking: [ { value: '1 is significantly better than 2', label: '1 is significantly better than 2'}, { value: '1 is better than 2', label: '1 is better than 2'}, { value: '1 is only slightly better than 2', label: '1 is only slightly better than 2'}, { value: '1 is the same as 2', label: '1 is the same as 2'}, { value: '2 is only slightly better than 1', label: '2 is only slightly better than 1'}, { value: '2 is better than 1', label: '2 is better than 1'}, { value: ' 2 is significantly better than 1', label: ' 2 is significantly better than 1'} ]
  };

  // --- HELPER FUNCTIONS ---
  const findOptionByValue = (optionsArray, value) => optionsArray?.find(option => option.value === value) || null;
  
  const taskLockedAlert = () => {
    const descriptor = window.Utterancedata?.descriptor;
    if (!descriptor) return;
    let lockTxt = "You don't own this task on its current state. Therefore annotations can't be created or modified.";
    if (descriptor.task_can_claim) {
      lockTxt += `<br><br>You may <b>claim</b> the task and become the task owner as <b>${descriptor.task_role}</b>`;
    }
    frm.myAlert('<i class="fa fa-lock alert-icon"></i> Task Locked', lockTxt);
  };
  
  // UPDATED: Now targets the correct data location for reset
  const resetFormToInitialState = useCallback(() => {
    // Reset local UI state
    setCurrentRatings({ response1: {}, response2: {} });
    setCurrentResponseRanking(null);
    setCurrentExplanation('');
    setCurrentCorrectedResponse1('');
    setCurrentCitation1('');
    setCurrentCorrectedResponse2('');
    setCurrentCitation2('');
    
    // Reset the persistent data in the correct task-specific object
    const annots = window.Utterancedata?.responseGenAnnotations;
    if (annots && allPrompts.length > 0) {
        annots.responseProject = Array(allPrompts.length).fill(null).map(() => ({}));
        if (typeof window.updateAnnotations === 'function') {
            window.updateAnnotations();
        }
    }
  }, [allPrompts]);

  // --- VALIDATION AND EXTERNAL CONTROLS ---
  const validateDropdowns = () => {
    const requiredCategories = Object.keys(ratingOptions).filter(k => k !== 'ranking');
    for (const resp of ['response1', 'response2']) {
      for (const cat of requiredCategories) {
        if (!currentRatings[resp][cat]) {
          frm.myAlert('Missing Rating', `Please select a value for <b>${cat.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</b> in <b>${resp.replace('response', 'Response ')}</b>.`);
          return false;
        }
      }
    }
     if (!currentResponseRanking) {
      frm.myAlert('Missing Ranking', 'Please select a ranking value before proceeding.');
      return false;
    }
    return true;
  };

  useImperativeHandle(ref, () => ({
    validateDropdowns,
    resetCurrentForm: resetFormToInitialState
  }));

  // --- LIFECYCLE HOOKS ---

  // NEW: Robustly initialize the annotation data structure on component load.
  // This prevents errors if a task is loaded for the first time.
  useEffect(() => {
    if (isInitialized && window.Utterancedata) {
      // 1. Ensure the main container object exists.
      if (!window.Utterancedata.responseGenAnnotations) {
        console.log("Initializing 'responseGenAnnotations' object for the first time.");
        window.Utterancedata.responseGenAnnotations = {};
      }
      
      const annots = window.Utterancedata.responseGenAnnotations;

      // 2. Ensure the responseProject array exists and has the correct length.
      // This also handles cases where new prompts are added to an existing task.
      if (!annots.responseProject || annots.responseProject.length !== allPrompts.length) {
        console.log(`Initializing/resizing 'responseProject' array to length ${allPrompts.length}.`);
        // Create a new array, but try to preserve existing data if any.
        const newResponseProject = Array(allPrompts.length).fill(null).map(() => ({}));
        if(annots.responseProject) {
            for(let i = 0; i < Math.min(annots.responseProject.length, allPrompts.length); i++) {
                newResponseProject[i] = annots.responseProject[i];
            }
        }
        annots.responseProject = newResponseProject;
      }
    }
  }, [isInitialized, allPrompts.length]);

  // UPDATED: This useEffect now loads data from the correct task-specific location.
  useEffect(() => {
    console.log(`Loading data for index ${currentIndex}...`);
    
    // 1. Get the data from the correct location: `responseGenAnnotations.responseProject`
    const responseProject = window.Utterancedata?.responseGenAnnotations?.responseProject;
    const currentData = responseProject?.[currentIndex] || {};

    // 2. Derive the local UI state from `currentData`. This logic is unchanged.
    const getRatingsFor = (responseType) => {
        const ratings = {};
        Object.keys(ratingOptions).filter(k => k !== 'ranking').forEach(category => {
            const key = `${responseType}_${category}`;
            const savedValue = currentData[key];
            ratings[category] = savedValue ? findOptionByValue(ratingOptions[category], savedValue) : null;
        });
        return ratings;
    };
    
    // 3. Set all local states in one block. This will now correctly load saved data or clear the form.
    setCurrentRatings({ response1: getRatingsFor('response1'), response2: getRatingsFor('response2') });
    setCurrentResponseRanking(findOptionByValue(ratingOptions.ranking, currentData.responseRanking) || null);
    setCurrentExplanation(currentData.explanation || '');
    setCurrentCorrectedResponse1(currentData.correctedResponse1 || '');
    setCurrentCitation1(currentData.citation1 || '');
    setCurrentCorrectedResponse2(currentData.correctedResponse2 || '');
    setCurrentCitation2(currentData.citation2 || '');

  }, [currentIndex, allPrompts]); // Re-run when navigating between prompts.

  // Lock checking interval
  useEffect(() => {
    const lockCheckInterval = setInterval(() => {
      const currentLockStatus = isTaskLocked();
      if (currentLockStatus !== isLocked) setIsLocked(currentLockStatus);
    }, 500);
    return () => clearInterval(lockCheckInterval);
  }, [isLocked]);

  // UPDATED: This callback now saves data to the correct task-specific location.
  const handleValueChange = useCallback((key, value, isSelect = false) => {
    if (isLocked) { taskLockedAlert(); return; }
    
    // 1. Point to the root object for our annotations
    const responseGenAnnotations = window.Utterancedata?.responseGenAnnotations;
    if (!responseGenAnnotations?.responseProject) {
        console.error("Cannot save: responseGenAnnotations.responseProject is not initialized.");
        return;
    }

    // --- Update local UI state (no changes here) ---
    const updateNestedState = (responseKey, category, val) => {
        setCurrentRatings(prev => ({ ...prev, [responseKey]: { ...prev[responseKey], [category]: val } }));
    };

    if (key.startsWith('response1_')) {
      const category = key.replace('response1_', '');
      updateNestedState('response1', category, value);
    } else if (key.startsWith('response2_')) {
      const category = key.replace('response2_', '');
      updateNestedState('response2', category, value);
    } else if (key === 'responseRanking') {
      setCurrentResponseRanking(value);
    } else {
      const setters = {
        explanation: setCurrentExplanation, correctedResponse1: setCurrentCorrectedResponse1, citation1: setCurrentCitation1,
        correctedResponse2: setCurrentCorrectedResponse2, citation2: setCurrentCitation2
      };
      if (setters[key]) setters[key](value);
    }
    // --- End local state update ---

    // 2. Prepare the value to be saved in the database
    const valueToSave = isSelect && value ? value.value : value;
    const currentPromptData = allPrompts[currentIndex];

    // 3. Get the specific annotation object for the current prompt from the CORRECT location
    if (!responseGenAnnotations.responseProject[currentIndex]) {
      responseGenAnnotations.responseProject[currentIndex] = {};
    }
    const annotation = responseGenAnnotations.responseProject[currentIndex];

    // 4. Save static and dynamic data to the correct annotation object
    if (!annotation.Prompt) {
        annotation.Prompt = currentPromptData.prompt;
        annotation.Response1 = currentPromptData.response1;
        annotation.Response2 = currentPromptData.response2;
    }
    annotation[key] = valueToSave;

    // 5. Trigger the global save function, which will now send the correctly updated UtteranceData
    if (typeof window.updateAnnotations === 'function') window.updateAnnotations();

  }, [isLocked, currentIndex, allPrompts]);
  
  const handleLockedInteraction = () => { if (isLocked) { taskLockedAlert(); } };

  // --- RENDER LOGIC ---
  const customSelectStyles = {
    control: (provided, state) => ({ ...provided, backgroundColor: '#fff', borderColor: state.isFocused ? '#805ad5' : '#d1d5db', boxShadow: state.isFocused ? '0 0 0 2px #e9d8fd' : 'none', borderRadius: 8, minHeight: 40, fontSize: 16, transition: 'border-color 0.2s', '&:hover': { borderColor: '#805ad5' } }),
    option: (provided, state) => ({ ...provided, backgroundColor: state.isSelected ? '#805ad5' : state.isFocused ? '#f3e8ff' : '#fff', color: state.isSelected ? '#fff' : '#222', fontWeight: state.isSelected ? 600 : 400, fontSize: 16, cursor: 'pointer' }),
    menu: (provided) => ({ ...provided, borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', zIndex: 20 }),
    placeholder: (provided) => ({ ...provided, color: '#a0aec0', fontSize: 16 }),
    singleValue: (provided) => ({ ...provided, color: '#222', fontSize: 16 }),
    input: (provided) => ({ ...provided, fontSize: 16 }),
  };

  const Title = ({ text, colorClass }) => ( <div className="card-title"><span className={`dot ${colorClass}`}></span><h3 className="title-text">{text}</h3></div> );
  
  const currentPromptData = allPrompts[currentIndex];
  if (!isInitialized || !currentPromptData) {
    return <div className="loading-container">Waiting for prompt data...</div>;
  }
  
  const showCorrectionForResponse1 = true;
  const showCorrectionForResponse2 = true;

  // The JSX is unchanged because it correctly reads from local state.
  return (
    <div className="comparison-container bl">
      <div className="card prompt-card">
        <Title text="Prompt" colorClass="dot-purple" />
        <textarea value={currentPromptData.prompt} className="card-textarea" readOnly />
      </div>

      <div className="responses-grid">
        <div className="card response-card response-card-1">
          <Title text="Response 1" colorClass="dot-green" />
          <textarea value={currentPromptData.response1} className="card-textarea" readOnly />
          <div className="ratings-section">
            {Object.keys(ratingOptions).filter(k => k !== 'ranking').map((category) => (
              <div className="rating-row" key={`r1-${category}`}>
                <Title text={category.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} colorClass="dot-green" />
                <Select
                  value={currentRatings.response1[category]}
                  onFocus={handleLockedInteraction}
                  onClick={handleLockedInteraction}
                  onChange={(option) => handleValueChange(`response1_${category}`, option, true)}
                  options={ratingOptions[category]}
                  styles={customSelectStyles}
                  placeholder="Select Rating"
                />
              </div>
            ))}
          </div>
        </div>
        <div className="card response-card response-card-2">
          <Title text="Response 2" colorClass="dot-blue" />
          <textarea value={currentPromptData.response2} className="card-textarea" readOnly />
          <div className="ratings-section">
            {Object.keys(ratingOptions).filter(k => k !== 'ranking').map((category) => (
              <div className="rating-row" key={`r2-${category}`}>
                <Title text={category.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} colorClass="dot-blue" />
                <Select
                  value={currentRatings.response2[category]}
                  onFocus={handleLockedInteraction}
                  onClick={handleLockedInteraction}
                  onChange={(option) => handleValueChange(`response2_${category}`, option, true)}
                  options={ratingOptions[category]}
                  styles={customSelectStyles}
                  placeholder="Select Rating"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="card prompt-card">
        <Title text="Response Ranking" colorClass="dot-purple" />
        <Select
          value={currentResponseRanking}
          onFocus={handleLockedInteraction}
          onClick={handleLockedInteraction}
          onChange={(option) => handleValueChange('responseRanking', option, true)}
          options={ratingOptions.ranking}
          styles={customSelectStyles}
          placeholder="Which response is better?"
        />
      </div>
      
      <div className="card prompt-card">
        <Title text="Explanation Supporting Preferred Response" colorClass="dot-purple" />
        <textarea 
          value={currentExplanation} 
          onChange={(e) => handleValueChange('explanation', e.target.value)} 
          placeholder="Explain your reasoning for the ranking..." 
          className="card-textarea" 
          readOnly={isLocked} 
          onClick={handleLockedInteraction} 
          onFocus={handleLockedInteraction} 
        />
      </div>
      
      {(showCorrectionForResponse1 || showCorrectionForResponse2) && (
        <div className="responses-grid">
          {showCorrectionForResponse1 && (
            <div className="card response-card response-card-1">
              <Title text="Errors" />
              <Title text="Corrected Response 1" colorClass="dot-green" />
              <textarea 
                value={currentCorrectedResponse1} 
                onChange={(e) => handleValueChange('correctedResponse1', e.target.value)} 
                placeholder="If needed, provide a corrected version..." 
                className="card-textarea" 
                readOnly={isLocked}
                onClick={handleLockedInteraction} 
                onFocus={handleLockedInteraction} 
              />
              <Title text="Citation 1" colorClass="dot-green" />
              <textarea 
                value={currentCitation1} 
                onChange={(e) => handleValueChange('citation1', e.target.value)} 
                placeholder="Provide citations..." 
                className="card-textarea" 
                readOnly={isLocked}
                onClick={handleLockedInteraction} 
                onFocus={handleLockedInteraction} 
              />
            </div>
          )}
          
          {showCorrectionForResponse2 && (
            <div className="card response-card response-card-2">
              <Title text="Errors" />
              <Title text="Corrected Response 2" colorClass="dot-blue" />
              <textarea 
                value={currentCorrectedResponse2} 
                onChange={(e) => handleValueChange('correctedResponse2', e.target.value)} 
                placeholder="If needed, provide a corrected version..." 
                className="card-textarea" 
                readOnly={isLocked}
                onClick={handleLockedInteraction} 
                onFocus={handleLockedInteraction} 
              />
              <Title text="Citation 2" colorClass="dot-blue" />
              <textarea 
                value={currentCitation2} 
                onChange={(e) => handleValueChange('citation2', e.target.value)} 
                placeholder="Provide citations..." 
                className="card-textarea" 
                readOnly={isLocked}
                onClick={handleLockedInteraction} 
                onFocus={handleLockedInteraction} 
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default ComparisonTemplate;